import { v4 as uuidv4 } from "uuid";
import ApiRequest from "../../common/lib/apiRequest";
import PROJECTS from "@/common/config/projects";
import AppConst from "@/common/constants";

const ICONS = {
  confirmed: require("@/assets/icons/tx-confirmed.svg"),
  sent: require("@/assets/icons/asset-sent.svg"),
  received: require("@/assets/icons/asset-received.svg"),
  updated: require("@/assets/icons/strategy-updated.svg"),
};
const NOTI_DISAPEAR_TIMEOUT = 8000;

const mutationTypes = {
  ADD_ALERT_NOTIFICATION: "notification/ADD_ALERT_NOTIFICATION",
  REMOVE_ALERT_NOTIFICATION: "notification/REMOVE_ALERT_NOTIFICATION",
  ADD_NOTIFICATION: "notification/ADD_NOTIFICATION",
  READ_NOTIFICATION: "notification/READ_NOTIFICATION",
  SET_NOTIFICATIONS: "notification/SET_NOTIFICATIONS",
  ADD_RAW_TX_EVENT: "notification/ADD_RAW_TX_EVENT",
};

const state = {
  notifications: [],
  alertNotifications: [],
  rawTxEvents: [],
};

const actions = {
  async fetchNotifications({ commit }) {
    try {
      const result = await ApiRequest.get("/notification");
      const notifications = result
        .map(mapNoti)
        .filter(noti => !!noti)
        .sort((a, b) => b.createdAt - a.createdAt);
      commit(mutationTypes.SET_NOTIFICATIONS, notifications);
    } catch (error) {
      console.error(error);
    }
  },
  async readNoti({ commit }, noti) {
    commit(mutationTypes.READ_NOTIFICATION, { id: noti.id, read: true });
    try {
      await ApiRequest.patch(`/notification/${noti.originType}/${noti.id}`, {
        markAsRead: true,
      });
    } catch (error) {
      console.error(error);
      commit(mutationTypes.READ_NOTIFICATION, { id: noti.id, read: false });
    }
  },
  addAlertNoti({ commit, dispatch }, noti) {
    if (noti.type === "transaction") {
      commit(mutationTypes.ADD_RAW_TX_EVENT, noti);
    }
    const options = mapNoti({
      ...noti,
      metadata: noti.tx,
    });
    if (!options) return;
    options.expiry =
      new Date().valueOf() + (options.timeout || NOTI_DISAPEAR_TIMEOUT);
    options.id = uuidv4();
    commit(mutationTypes.ADD_ALERT_NOTIFICATION, options);
    dispatch("fetchNotifications");
  },
  removeAlertNoti({ commit }, id) {
    commit(mutationTypes.REMOVE_ALERT_NOTIFICATION, id);
  },
};

const mutations = {
  [mutationTypes.ADD_ALERT_NOTIFICATION](state, options) {
    state.alertNotifications = [options, ...state.alertNotifications];
  },
  [mutationTypes.REMOVE_ALERT_NOTIFICATION](state, id) {
    const alertNotifications = [...state.alertNotifications];
    const index = alertNotifications.findIndex(al => al.id === id);
    alertNotifications.splice(index, 1);
    state.alertNotifications = alertNotifications;
  },
  [mutationTypes.SET_NOTIFICATIONS](state, data) {
    state.notifications = data;
  },
  [mutationTypes.READ_NOTIFICATION](state, { id, read }) {
    const notifications = [...state.notifications];
    const notiIndex = notifications.findIndex(noti => noti.id === id);
    if (notiIndex === -1) return;
    notifications.splice(notiIndex, 1, { ...notifications[notiIndex], read });
    state.notifications = notifications;
  },
  [mutationTypes.ADD_RAW_TX_EVENT](state, event) {
    state.rawTxEvents = [event, ...state.rawTxEvents];
  },
};

const getters = {
  alertNotifications: state => state.alertNotifications,
  notifications: state => state.notifications,
  unreadCount: state => state.notifications.filter(noti => !noti.read).length,
  isTxConfirmed: state => txHash => {
    return (
      state.rawTxEvents.findIndex(
        event =>
          event.status &&
          String(event.tx?.txHash).toLowerCase() ===
            String(txHash).toLowerCase()
      ) > -1
    );
  },
};

const mapNoti = rawNoti => {
  try {
    const noti = {
      id: rawNoti.id,
      createdAt: rawNoti.createdAt || Date.now(),
      read: rawNoti.isRead,
      icon: ICONS["updated"],
      type: "",
      title: "",
      message: "",
      metadata: {},
      originType: rawNoti.type,
    };
    if (rawNoti.type !== "transaction") {
      noti.type = "annoucement";
      noti.title = rawNoti.name || "Unagii annoucement";
      noti.message = rawNoti.content;
    } else {
      noti.type = "transaction";
      noti.icon = ICONS["confirmed"];
      switch (rawNoti.txType) {
        case AppConst.TX_TYPES.VAULT: {
          if (
            ["deposit", "withdraw", "migrate"].indexOf(
              rawNoti.metadata.type
            ) === -1
          )
            return null;
          noti.title = `notification.uvault.${rawNoti.metadata.type}.title`;
          noti.message = `notification.uvault.${rawNoti.metadata.type}.message`;
          noti.link = `https://etherscan.io/tx/${rawNoti.metadata.txHash}`;
          noti.metadata = {
            ...(rawNoti.metadata.metadata || {}),
            amount: Number(
              rawNoti.metadata.metadata.realAmount ||
                rawNoti.metadata.metadata.amount
            ).toLocaleString(),
            token: rawNoti.metadata.metadata.token.toUpperCase(),
            vault:
              AppConst.VAULT_MAP[rawNoti.metadata.metadata.vault] ||
              rawNoti.metadata.metadata.vault,
            vaultNew:
              AppConst.VAULT_MAP[rawNoti.metadata.metadata.vaultNew] ||
              rawNoti.metadata.metadata.vaultNew,
          };
          break;
        }
        case AppConst.TX_TYPES.KYBER:
        case AppConst.TX_TYPES.ETH_STAKING:
        case AppConst.TX_TYPES.NONETH_STAKING:
        case AppConst.TX_TYPES.TENDERMINT: {
          if (!rawNoti.metadata.type) return null;
          if (
            ["approve-old-knc", "approve", "legacy-withdraw"].includes(
              rawNoti.metadata.type
            )
          ) {
            return null;
          }
          const project = PROJECTS.find(
            pr =>
              pr.projectName ===
              (rawNoti.metadata.network?.toLowerCase() || rawNoti.txType)
          );
          if (!project) return;
          let notiKey = AppConst.TX_TYPE_TO_NOTI_KEY[rawNoti.txType];
          if (
            (rawNoti.txType === AppConst.TX_TYPES.ETH_STAKING ||
              rawNoti.txType === AppConst.TX_TYPES.NONETH_STAKING) &&
            rawNoti.metadata.network
          ) {
            notiKey += "." + rawNoti.metadata.network.toLowerCase();
          }
          noti.title = `notification.stake.${notiKey}.${rawNoti.metadata.type}.title`;
          noti.message = `notification.stake.${notiKey}.${rawNoti.metadata.type}.message`;
          noti.link = `${project.config.explorerBridgeLink || project.config.explorerTxsLink}${rawNoti.metadata.txHash}`;
          noti.metadata = {
            ...(rawNoti.metadata.metadata || {}),
            amount: Number(rawNoti.metadata.metadata?.amount || rawNoti.metadata.amount).toLocaleString(),
            token: rawNoti.metadata?.token?.toUpperCase(),
            network: project.text,
            daysLocked: project.staking.daysLocked,
          };
          break;
        }
        default:
          break;
      }
    }
    return noti;
  } catch (error) {
    console.log(error)
    return null;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
